import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, ArrowRight, ShieldCheck, CreditCard } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const DH_DESIGN_LOGO = 'https://customer-assets.emergentagent.com/job_3ded9da4-7891-48ae-9b29-bf144d630c6e/artifacts/f6qq4dmm_1000022581.png';
const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_3ded9da4-7891-48ae-9b29-bf144d630c6e/artifacts/76ag0ln1_1000021182.png';

/**
 * /signup — Paid trial signup (May 11, 2026)
 *
 * Anonymous flow for users coming from /pricing. Collects minimal account
 * info → POST /api/auth/register → on success auto-logs them in →
 * POST /api/billing/checkout → redirects to Stripe Checkout (which
 * collects the card and starts the 14-day trial).
 *
 * This page is paired with /register (the waitlist) but they are
 * intentionally separate paths:
 *   /signup         → card required → instant access via Stripe trial
 *   /register       → admin-approved waitlist → no card → override grant
 */
export default function Signup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const plan = params.get('plan') || 'professional';
  const interval = params.get('interval') || 'monthly';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [plansLoaded, setPlansLoaded] = useState(null);

  // Fetch plan details so we can show "you're starting <Tier>" + price id
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/billing/plans`);
        const match = (r.data?.plans || []).find((p) => p.id === plan);
        setPlansLoaded(match || null);
      } catch (e) {
        // soft-fail; we just won't show the plan label
        setPlansLoaded(null);
      }
    })();
  }, [plan]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      // Step 1: create account
      const registerRes = await axios.post(`${API}/api/auth/register`, {
        email,
        password,
        name,
        company: company || null,
        role: 'sales_rep',
      });
      const data = registerRes.data || {};

      if (data.pending) {
        // Should not happen for non-first users — they need admin approval before
        // they can authenticate. Direct them to the waitlist messaging instead.
        toast.error(
          'Your account needs admin approval before checkout. Please use the Request Access page.'
        );
        navigate('/register');
        return;
      }
      if (!data.token) {
        toast.error('Could not create account. Please try again.');
        return;
      }

      // Step 2: auto-login
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      // Step 3: kick off Stripe checkout for the chosen plan
      const expectedPriceId =
        interval === 'yearly' ? plansLoaded?.yearly_price_id : plansLoaded?.monthly_price_id;
      const checkoutRes = await axios.post(
        `${API}/api/billing/checkout`,
        {
          tier: plan,
          interval,
          success_path: '/billing/return',
          cancel_path: '/pricing',
          ...(expectedPriceId ? { expected_price_id: expectedPriceId } : {}),
        },
        { headers: { Authorization: `Bearer ${data.token}` } }
      );
      if (checkoutRes.data?.checkout_url) {
        window.location.href = checkoutRes.data.checkout_url;
      } else {
        toast.error('Could not start checkout — please try from the Pricing page.');
        navigate('/pricing');
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || 'Signup failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background" data-testid="signup-page">
      {/* Left - Image Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(20,20,20,0.6), rgba(20,20,20,0.4))' }} />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h1 className="font-serif text-4xl lg:text-5xl leading-tight mb-3">
            Start your 14-day free trial
          </h1>
          <p className="text-white/85 max-w-md mb-4">
            Card required — not charged for 14 days. Cancel anytime from your billing portal.
          </p>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: '#C9A84C' }}>
            — DH Design
          </div>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md space-y-8">
          <div className="flex justify-center lg:justify-start">
            <img src={DH_DESIGN_LOGO} alt="DH Design" className="h-16 w-16 object-contain" />
          </div>

          <div className="space-y-3">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Start free trial
            </p>
            <h2 className="font-serif text-3xl lg:text-4xl">
              {plansLoaded ? `${plansLoaded.name} · 14 days free` : 'Create your account'}
            </h2>
            <p className="text-muted-foreground text-sm">
              Card required — not charged for 14 days. Cancel anytime.
            </p>
          </div>

          {/* Trust strip */}
          <div className="flex flex-col gap-2 rounded-sm border border-[#E8E6E1] bg-white/60 backdrop-blur p-3">
            <div className="flex items-center gap-2 text-xs text-stone-700">
              <CreditCard className="h-3.5 w-3.5 text-[#B89B5E]" />
              <span>Card collected on next step via Stripe</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-stone-700">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
              <span>No charge until trial ends — cancel anytime</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="signup-form">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs uppercase tracking-wider">Full Name</Label>
              <Input
                id="name" type="text" placeholder="John Smith"
                value={name} onChange={(e) => setName(e.target.value)} required
                className="h-12 rounded-sm" data-testid="signup-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider">Email Address</Label>
              <Input
                id="email" type="email" placeholder="name@company.com"
                value={email} onChange={(e) => setEmail(e.target.value)} required
                className="h-12 rounded-sm" data-testid="signup-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company" className="text-xs uppercase tracking-wider">
                Company <span className="text-muted-foreground">(Optional)</span>
              </Label>
              <Input
                id="company" type="text" placeholder="Your company name"
                value={company} onChange={(e) => setCompany(e.target.value)}
                className="h-12 rounded-sm" data-testid="signup-company-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs uppercase tracking-wider">Password</Label>
                <Input
                  id="password" type="password" placeholder="Min. 6 chars"
                  value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="h-12 rounded-sm" data-testid="signup-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-xs uppercase tracking-wider">Confirm</Label>
                <Input
                  id="confirmPassword" type="password" placeholder="Confirm"
                  value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
                  className="h-12 rounded-sm" data-testid="signup-confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 rounded-sm uppercase tracking-widest text-xs font-medium bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
              disabled={loading}
              data-testid="signup-submit-btn"
            >
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating account…</>
              ) : (
                <>Continue to Stripe — enter card<ArrowRight className="ml-2 h-4 w-4" /></>
              )}
            </Button>

            <p className="text-[10px] text-center text-muted-foreground">
              Card required · not charged for 14 days · cancel anytime
            </p>
          </form>

          <div className="pt-6 border-t border-border space-y-3">
            <p className="text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-foreground hover:text-[#B89B5E] transition-colors underline underline-offset-4" data-testid="signup-signin-link">
                Sign in
              </Link>
            </p>
            <p className="text-center text-[11px] text-muted-foreground">
              Want admin-approved early access without a card?{' '}
              <Link to="/register" className="hover:text-foreground transition-colors underline underline-offset-4" data-testid="signup-waitlist-link">
                Request access →
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
